import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  ShieldCheck,
  AlertTriangle,
  FileSearch,
  Trash2,
  ShieldAlert,
  Sparkles,
  ClipboardCheck,
  Copy,
  Download,
  CheckCircle2,
  FilePlus,
} from 'lucide-react';
import AdminStatusStrip from '../components/AdminStatusStrip';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_KEYS = [
  'case_summary',
  'legal_status_overview',
  'identity_document_consistency',
  'missing_documents',
  'critical_deadlines',
  'cross_border_reference_flags',
  'return_appeal_renewal_path',
  'risk_compliance_flags',
  'human_review_points',
  'recommended_next_step',
];

const SEVERITY_COLOR = {
  low:      { bg: 'rgba(125,211,252,0.08)', border: '#7DD3FC', fg: '#7DD3FC' },
  medium:   { bg: 'rgba(245,158,11,0.08)',  border: '#F59E0B', fg: '#F59E0B' },
  high:     { bg: 'rgba(249,115,22,0.10)',  border: '#FB923C', fg: '#FB923C' },
  critical: { bg: 'rgba(239,68,68,0.10)',   border: '#F87171', fg: '#F87171' },
};

const SeverityChip = ({ severity }) => {
  const { t } = useTranslation();
  const cfg = SEVERITY_COLOR[severity] || SEVERITY_COLOR.medium;
  return (
    <span
      data-testid={`casehub-severity-${severity}`}
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: cfg.fg,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.5,
        marginLeft: 8,
        verticalAlign: 'middle',
      }}
    >
      {t(`caseHubAdmin.severity.${severity}`, severity)}
    </span>
  );
};

const HumanReviewBadge = () => {
  const { t } = useTranslation();
  return (
    <span
      data-testid="casehub-human-review-badge"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 10px', borderRadius: 999,
        border: '1px solid rgba(245,158,11,0.35)',
        background: 'rgba(245,158,11,0.08)',
        color: '#F59E0B',
        fontSize: 10,
        letterSpacing: 0.4,
        fontWeight: 700,
      }}
    >
      <ShieldAlert className="w-3 h-3" />
      {t('caseHubAdmin.humanReviewBadge')}
    </span>
  );
};

/**
 * Derive a safe, audit-friendly download filename for a case brief.
 *
 * Strict rules:
 *  • Filename pattern: `hussarai-case-<CASE_ID>-YYYY-MM-DD.md` when a case ID
 *    is detected; `hussarai-case-YYYY-MM-DD.md` when no ID is found.
 *  • CASE_ID is harvested ONLY from analyzer-visible text (case_summary,
 *    submitted_excerpt, recommended_next_step) and only when it matches the
 *    safe placeholder pattern `[A-Z][A-Z0-9]{1,9}-\d{1,6}` (e.g. DEMO-001).
 *  • CASE_ID is then sanitised — everything outside `[A-Za-z0-9_-]` is
 *    stripped and the result is truncated to 24 chars. Real PESELs, passport
 *    numbers and full date strings cannot survive this filter.
 *  • Date is derived from `new Date()` in ISO `YYYY-MM-DD` form.
 *
 * Exported so static tests can scan + cover it.
 */
export const _CASE_ID_RX = /\b([A-Z][A-Z0-9]{1,9}-\d{1,6})\b/;

export const sanitiseCaseId = (raw) => {
  if (typeof raw !== 'string') return '';
  // Strip anything outside the safe charset.
  const cleaned = raw.replace(/[^A-Za-z0-9_-]/g, '');
  return cleaned.slice(0, 24);
};

export const buildCaseBriefFilename = (result, now = new Date()) => {
  const iso = now.toISOString().slice(0, 10); // YYYY-MM-DD
  if (!result || typeof result !== 'object') {
    return `hussarai-case-${iso}.md`;
  }
  // Scan only safe analyzer-visible fields for an obvious case-ref token.
  const hay = [
    result.case_summary,
    result.submitted_excerpt,
    result.recommended_next_step,
  ].filter((v) => typeof v === 'string').join(' \n ');
  const m = hay.match(_CASE_ID_RX);
  const id = m ? sanitiseCaseId(m[1]) : '';
  return id ? `hussarai-case-${id}-${iso}.md` : `hussarai-case-${iso}.md`;
};

/**
 * Build the Markdown "case brief" from an analyzer result.
 *
 * Strict safety rules:
 *  • Adds NO new conclusions — only reformats fields returned by the analyzer.
 *  • Never references admin routes, API endpoints, cookies, IDs, or any
 *    field beyond the well-known structural keys.
 *  • Each section that is empty / missing renders the safe fallback line
 *    "Brak danych do oceny — wymaga weryfikacji." / "No data available for
 *    assessment — requires review."
 *  • Uses the doctrine + a short disclaimer string copied verbatim from i18n
 *    (no AI-decision language ever introduced by the formatter).
 *
 * The function is exported alongside the page so static tests can scan it.
 */
/*
 *  ----------------------------------------------------------------------
 *  Persisted-case loader contract (Phase 2.3.x follow-up)
 *  ----------------------------------------------------------------------
 *
 *  Reads `?case_id=<case_<32 hex>>` from the URL. If present and well-
 *  formed, calls the existing Phase 2.1 read endpoints to assemble a
 *  ``caseRecord`` that the locked ``isSignedOffRecord`` predicate can
 *  evaluate. The loader NEVER writes, NEVER analyzes, NEVER signs off,
 *  NEVER toggles state, and NEVER constructs an audit-anchored shape
 *  from local state. It only mirrors what the server already persisted.
 *
 *  Endpoint contract used (READS only):
 *
 *    GET /api/admin/immigration-cases/{case_id}                — status
 *    GET /api/admin/immigration-cases/{case_id}/audit-events   — chain
 */
const CASE_ID_PATTERN = /^case_[0-9a-f]{32}$/;

export const readCaseIdFromLocation = (locationLike) => {
  try {
    const search = (locationLike && locationLike.search) || '';
    const params = new URLSearchParams(search);
    const raw = params.get('case_id');
    if (!raw) return null;
    return CASE_ID_PATTERN.test(raw) ? raw : null;
  } catch (_e) {
    return null;
  }
};

/*
 *  Shape the merged record so the locked ``isSignedOffRecord`` predicate
 *  can evaluate it without any inference. When the persisted case is not
 *  audit-anchored, we still merge the audit-event metadata best-effort so
 *  the predicate returns false and the working-brief label remains.
 */
export const mergePersistedRecord = (
  caseDoc, auditEventsResponse, versionsResponse,
) => {
  if (!caseDoc || typeof caseDoc !== 'object') return null;
  const events = (auditEventsResponse && Array.isArray(auditEventsResponse.items))
    ? auditEventsResponse.items
    : [];

  // Safely extract ONLY the amendment cross-link metadata from the
  // latest version payload, if any. We deliberately avoid pulling the
  // entire payload onto ``caseRecord`` — only the two amends_* keys are
  // needed for the back-link, so the rest of the payload stays
  // server-side. If the case has no ``amends_case_id``, the back-link
  // simply does not render.
  const versions = (versionsResponse && Array.isArray(versionsResponse.items))
    ? versionsResponse.items : [];
  let amendsCaseId = null;
  let amendsVersionId = null;
  if (versions.length > 0) {
    const latest = versions[versions.length - 1];
    const payload = (latest && latest.payload && typeof latest.payload === 'object')
      ? latest.payload : null;
    if (payload && typeof payload.amends_case_id === 'string'
        && /^case_[0-9a-f]{32}$/.test(payload.amends_case_id)) {
      amendsCaseId = payload.amends_case_id;
      if (typeof payload.amends_version_id === 'string'
          && payload.amends_version_id.length > 0) {
        amendsVersionId = payload.amends_version_id;
      }
    }
  }

  // Find the most recent reviewer_signed_off event, if any. The endpoint
  // returns events sorted by timestamp ascending, so we walk from the end.
  let signoffEvent = null;
  for (let i = events.length - 1; i >= 0; i -= 1) {
    if (events[i] && events[i].event_type === 'reviewer_signed_off') {
      signoffEvent = events[i];
      break;
    }
  }
  if (!signoffEvent) {
    // Working-brief path — predicate will return false.
    return {
      case_id: caseDoc.case_id,
      status:  caseDoc.status,
      current_version_id: caseDoc.current_version_id,
      current_version_number: caseDoc.current_version_number,
      amends_case_id:    amendsCaseId,
      amends_version_id: amendsVersionId,
    };
  }
  return {
    case_id:               caseDoc.case_id,
    status:                caseDoc.status,
    current_version_id:    caseDoc.current_version_id,
    current_version_number: caseDoc.current_version_number,
    event_type:            signoffEvent.event_type,
    event_id:              signoffEvent.event_id,
    version_id:            signoffEvent.version_id,
    payload_hash:          signoffEvent.payload_hash,
    previous_payload_hash: signoffEvent.previous_payload_hash,
    hash_algorithm:        signoffEvent.hash_algorithm,
    signed_off_at_iso:     signoffEvent.timestamp,
    actor_role:            signoffEvent.actor_role,
    actor_ref:             signoffEvent.actor_ref,
    reason:                signoffEvent.reason,
    amends_case_id:        amendsCaseId,
    amends_version_id:     amendsVersionId,
  };
};

/*
 *  ----------------------------------------------------------------------
 *  Persisted-case audit-anchor contract (Phase 2.3.x)
 *  ----------------------------------------------------------------------
 *
 *  An audit-anchored block (UI pill + Markdown export header) may appear
 *  ONLY when the case record was fetched from the persisted backend and
 *  the metadata explicitly proves human-authority closure via the
 *  server-side audit chain. The four locked conditions must ALL hold:
 *
 *    - record.status        === "reviewed_signed_off"
 *    - record.event_type    === "reviewer_signed_off"
 *    - record.payload_hash  is a non-empty string
 *    - record.hash_algorithm === "HMAC-SHA256"
 *
 *  No client-side toggle, no inferred status, no derived authority. This
 *  predicate is the single client-side gate; the server-side endpoint at
 *  POST /api/admin/immigration-cases/{case_id}/sign-off remains the only
 *  way to produce a record that satisfies it.
 */
export const isSignedOffRecord = (record) => {
  if (!record || typeof record !== 'object') return false;
  return (
    record.status === 'reviewed_signed_off'
    && record.event_type === 'reviewer_signed_off'
    && typeof record.payload_hash === 'string'
    && record.payload_hash.length > 0
    && record.hash_algorithm === 'HMAC-SHA256'
  );
};

export const buildCaseBriefMarkdown = (result, t, caseRecord = null) => {
  if (!result) return '';
  const labels = t('caseHubAdmin.sectionLabels', { returnObjects: true }) || {};
  const empty = t('caseHubAdmin.copyBrief.emptyFallback');
  const lines = [];
  const push = (s) => lines.push(s);

  // Header — title, timestamp, doctrine + disclaimer block.
  push(`# ${t('caseHubAdmin.copyBrief.title')}`);
  push('');
  push(`*${t('caseHubAdmin.copyBrief.timestampLabel')}: ${new Date().toISOString()}*`);
  push('');
  push(`> **${t('caseHubAdmin.copyBrief.doctrineLabel')}:** ${t('caseHubAdmin.doctrineLine')}`);
  push('>');
  push(`> **${t('caseHubAdmin.copyBrief.disclaimerLabel')}:** ${t('caseHubAdmin.copyBrief.disclaimer')}`);
  push('');

  const signedOff = isSignedOffRecord(caseRecord);

  if (signedOff) {
    // Audit-anchored block — emitted only when the persisted record carries
    // the four locked HMAC-verified fields proving server-side human-
    // authority closure. Replaces the working-brief label for this artifact.
    const sLabels = t('caseHubAdmin.signedOff.fieldLabels', { returnObjects: true }) || {};
    push(`## ${t('caseHubAdmin.signedOff.exportHeading')}`);
    push('');
    push(t('caseHubAdmin.signedOff.exportIntro'));
    push('');
    const safe = (v) => (typeof v === 'string' && v.trim() ? v.trim() : empty);
    push(`- **${sLabels.signedOffAt || 'Audit timestamp (UTC)'}:** ${safe(caseRecord.signed_off_at_iso || caseRecord.timestamp)}`);
    push(`- **${sLabels.actorRole || 'Actor role'}:** ${safe(caseRecord.actor_role)}`);
    push(`- **${sLabels.actorRef || 'Actor audit reference'}:** ${safe(caseRecord.actor_ref)}`);
    push(`- **${sLabels.eventType || 'Audit event type'}:** ${safe(caseRecord.event_type)}`);
    push(`- **${sLabels.payloadHash || 'Payload hash'}:** \`${safe(caseRecord.payload_hash)}\``);
    push(`- **${sLabels.previousPayloadHash || 'Previous payload hash'}:** \`${safe(caseRecord.previous_payload_hash)}\``);
    push(`- **${sLabels.hashAlgorithm || 'Hash algorithm'}:** ${safe(caseRecord.hash_algorithm)}`);
    if (caseRecord.event_id) {
      push(`- **${sLabels.auditEventId || 'Audit event id'}:** ${safe(caseRecord.event_id)}`);
    }
    if (caseRecord.version_id) {
      push(`- **${sLabels.versionId || 'Version id'}:** ${safe(caseRecord.version_id)}`);
    }
    push('');
  } else {
    // Working-brief status pill — defensive Phase 1 boundary marker.
    // Honestly communicates that the artifact is a DRAFT for human review.
    // The brief is NOT a reviewer endorsement and NOT a final-state record.
    push(`**${t('caseHubAdmin.workingBrief.label')}**`);
    push('');
  }
  push('---');
  push('');

  const heading = (key) => `## ${labels[key] || key}`;

  const writeTextSection = (key) => {
    push(heading(key));
    push('');
    const v = result[key];
    push(typeof v === 'string' && v.trim() ? v.trim() : empty);
    push('');
  };

  const writeStrListSection = (key) => {
    push(heading(key));
    push('');
    const arr = Array.isArray(result[key]) ? result[key] : [];
    if (arr.length === 0) {
      push(empty);
    } else {
      arr.forEach((item) => push(`- ${item}`));
    }
    push('');
  };

  const writeObjListSection = (key) => {
    push(heading(key));
    push('');
    const arr = Array.isArray(result[key]) ? result[key] : [];
    if (arr.length === 0) {
      push(empty);
    } else {
      arr.forEach((item) => {
        const label = item.label || item.jurisdiction || '';
        const note = item.note || '';
        const when = item.when || '';
        const sev = item.severity || '';
        const parts = [];
        if (label) parts.push(`**${label}**`);
        if (when)  parts.push(when);
        if (note)  parts.push(note);
        const sevTag = sev ? ` _(${t(`caseHubAdmin.severity.${sev}`, sev)})_` : '';
        push(`- ${parts.join(' · ') || empty}${sevTag}`);
      });
    }
    push('');
  };

  // 10 structured sections — exact order from the spec.
  writeTextSection('case_summary');
  writeTextSection('legal_status_overview');
  writeObjListSection('identity_document_consistency');
  writeStrListSection('missing_documents');
  writeObjListSection('critical_deadlines');
  writeObjListSection('cross_border_reference_flags');
  writeTextSection('return_appeal_renewal_path');
  writeObjListSection('risk_compliance_flags');
  writeStrListSection('human_review_points');
  writeTextSection('recommended_next_step');

  // Audit trail — both formats.
  push('---');
  push('');
  push(`## ${t('caseHubAdmin.copyBrief.auditTextHeading')}`);
  push('');
  push('```');
  push((typeof result.audit_trail_text === 'string' && result.audit_trail_text.trim())
    ? result.audit_trail_text
    : empty);
  push('```');
  push('');
  push(`## ${t('caseHubAdmin.copyBrief.auditJsonHeading')}`);
  push('');
  push('```json');
  push(JSON.stringify(Array.isArray(result.audit_trail_timeline)
    ? result.audit_trail_timeline
    : [], null, 2));
  push('```');
  push('');

  return lines.join('\n');
};

const renderSection = (key, value, t) => {
  if (key === 'case_summary' || key === 'legal_status_overview'
      || key === 'return_appeal_renewal_path' || key === 'recommended_next_step') {
    return (
      <p
        style={{ margin: 0, fontSize: 14, lineHeight: 1.6, opacity: 0.92 }}
        data-testid={`casehub-section-${key}-text`}
      >
        {value || t('caseHubAdmin.noFindings')}
      </p>
    );
  }
  if (key === 'missing_documents' || key === 'human_review_points') {
    const items = Array.isArray(value) ? value : [];
    if (items.length === 0) {
      return <p style={{ margin: 0, opacity: 0.6 }}>{t('caseHubAdmin.noFindings')}</p>;
    }
    return (
      <ul
        style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}
        data-testid={`casehub-section-${key}-list`}
      >
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }
  if (key === 'identity_document_consistency'
      || key === 'cross_border_reference_flags'
      || key === 'risk_compliance_flags'
      || key === 'critical_deadlines') {
    const items = Array.isArray(value) ? value : [];
    if (items.length === 0) {
      return <p style={{ margin: 0, opacity: 0.6 }}>{t('caseHubAdmin.noFindings')}</p>;
    }
    return (
      <ul
        style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}
        data-testid={`casehub-section-${key}-list`}
      >
        {items.map((item, idx) => {
          const label = item.label || item.jurisdiction || '';
          const note = item.note || '';
          const when = item.when || '';
          const sev = item.severity || 'medium';
          return (
            <li key={`${label}-${note}-${when}-${idx}`}>
              {label && <strong>{label}</strong>}
              {when && <span style={{ opacity: 0.75 }}> · {when}</span>}
              {note && <span style={{ opacity: 0.85 }}> — {note}</span>}
              <SeverityChip severity={sev} />
            </li>
          );
        })}
      </ul>
    );
  }
  return null;
};

const ImmigrationCaseDashboard = () => {
  const { t, i18n } = useTranslation();
  const [caseText, setCaseText] = useState('');
  const [result, setResult] = useState(null);
  // Persisted-case record fetched from the backend. When non-null and
  // ``isSignedOffRecord(caseRecord)`` is true, the status pill + Markdown
  // export switch into the audit-anchored mode. There is no client-side
  // setter for the "audit-anchored" status — it can only flip via a
  // persisted server record carrying the four locked HMAC-verified fields.
  const [caseRecord, setCaseRecord] = useState(null);
  // Loader state — only used when a ``?case_id=...`` URL param is present
  // and matches the opaque shape. Never written from any user action other
  // than the URL-driven effect below.
  const [loaderState, setLoaderState] = useState('idle'); // 'idle' | 'loading' | 'loaded' | 'error'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copyState, setCopyState] = useState('idle'); // 'idle' | 'copied' | 'fallback'
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saved'

  // Dry-run review state — driven ONLY by the explicit "Preview review packet"
  // button below. Never runs automatically. Never mutates persistence.
  const [dryRunState, setDryRunState] = useState('idle'); // 'idle' | 'loading' | 'loaded' | 'error'
  const [dryRunPacket, setDryRunPacket] = useState(null);
  const [dryRunCopyState, setDryRunCopyState] = useState('idle'); // 'idle' | 'copied' | 'fallback'

  // Signed-off PDF download state — driven ONLY by the explicit
  // "Download signed-off PDF" button. Never runs automatically. The
  // endpoint is GET and the server enforces the signed-off predicate.
  const [pdfDownloadState, setPdfDownloadState] = useState('idle'); // 'idle' | 'loading' | 'error'

  // Audit-chain verification state — driven ONLY by the explicit
  // "Verify audit chain" button. Never runs automatically. The endpoint
  // is a read-only composition over the existing audit-chain verifier
  // service.
  const [auditVerifyState, setAuditVerifyState] = useState('idle'); // 'idle' | 'loading' | 'loaded' | 'error'
  const [auditVerifyReport, setAuditVerifyReport] = useState(null);

  // Amendment workflow state — only visible for a signed-off case, only
  // submits on explicit click, only POSTs to the canonical amendment
  // endpoint. NEVER auto-fires on mount. NEVER calls sign-off / status /
  // assign / dry-run / PDF / email endpoints.
  const [amendmentPanelOpen, setAmendmentPanelOpen] = useState(false);
  const [amendmentReason, setAmendmentReason] = useState('');
  const [amendmentScope, setAmendmentScope] = useState('');
  const [amendmentState, setAmendmentState] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [amendmentResult, setAmendmentResult] = useState(null);
  const [amendmentError, setAmendmentError] = useState(null);

  const exampleItems = useMemo(() => {
    const items = t('caseHubAdmin.examples.items', { returnObjects: true });
    return Array.isArray(items) ? items : [];
  }, [t]);

  const canSubmit = useMemo(
    () => caseText.trim().length >= 10 && !loading,
    [caseText, loading],
  );

  // URL-driven persisted-case loader.
  //
  // When the dashboard is opened with `?case_id=case_<32 hex>` the loader
  // calls the EXISTING Phase 2.1 admin read endpoints to assemble a
  // ``caseRecord`` the locked ``isSignedOffRecord`` predicate can evaluate.
  // No write call is ever made. No analyze call is ever made. If no
  // ``case_id`` is present, this effect is a no-op and the Phase 1
  // paste/analyze workflow runs unchanged.
  useEffect(() => {
    if (typeof window === 'undefined') return; // SSR guard
    const targetId = readCaseIdFromLocation(window.location);
    if (!targetId) return; // no URL param → idle, Phase 1 path unchanged
    let cancelled = false;
    setLoaderState('loading');
    (async () => {
      try {
        // Three read-only admin endpoints fired in parallel. The
        // ``/versions`` call is what surfaces ``amends_case_id`` for the
        // amendment back-link — only the latest version payload is
        // consulted, and only the safe ``amends_case_id`` /
        // ``amends_version_id`` keys are merged onto ``caseRecord``. No
        // write endpoint is ever called.
        const [caseResp, eventsResp, versionsResp] = await Promise.all([
          axios.get(`${API}/admin/immigration-cases/${targetId}`, { withCredentials: true }),
          axios.get(`${API}/admin/immigration-cases/${targetId}/audit-events`, { withCredentials: true }),
          axios.get(`${API}/admin/immigration-cases/${targetId}/versions`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        const merged = mergePersistedRecord(
          caseResp.data, eventsResp.data, versionsResp.data,
        );
        setCaseRecord(merged);
        setLoaderState('loaded');
      } catch (_e) {
        if (cancelled) return;
        // Defensive: never echo backend internals into the UI; render
        // the safe i18n error string and stop.
        setCaseRecord(null);
        setLoaderState('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Memoised — recomputed only when the analysis result or the persisted
  // case record changes. The persisted record is the ONLY input that can
  // flip the brief into audit-anchored mode.
  const briefMarkdown = useMemo(
    () => buildCaseBriefMarkdown(result, t, caseRecord),
    [result, t, caseRecord],
  );

  const handleCopyBrief = useCallback(async () => {
    // Pure client-side. Never calls analyze. Never hits the backend.
    if (!briefMarkdown) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(briefMarkdown);
        setCopyState('copied');
        setTimeout(() => setCopyState((s) => (s === 'copied' ? 'idle' : s)), 2500);
        return;
      }
      setCopyState('fallback');
    } catch (_) {
      setCopyState('fallback');
    }
  }, [briefMarkdown]);

  const handleSaveBrief = useCallback(() => {
    // Pure client-side download. No network call, no backend, no cookie.
    // Blob → object URL → temporary <a download> → click → revoke.
    if (!briefMarkdown) return;
    try {
      const filename = buildCaseBriefFilename(result);
      const blob = new Blob([briefMarkdown], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.rel = 'noopener';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      setSaveState('saved');
      setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (_) {
      // Worst-case: just leave the state idle. The "Copy brief" path remains
      // available as a fallback for the same payload.
      setSaveState('idle');
    }
  }, [briefMarkdown, result]);

  const handleInsertExample = useCallback((key) => {
    const item = exampleItems.find((e) => e.key === key);
    if (!item) return;
    // Populate the textarea ONLY. Never auto-submit — reviewer stays in control.
    setCaseText(item.text);
    setResult(null);
    setError(null);
    setCopyState('idle');
    setSaveState('idle');
  }, [exampleItems]);

  const handleAnalyze = useCallback(async () => {
    setError(null);
    setLoading(true);
    setCopyState('idle');
    setSaveState('idle');
    try {
      const r = await axios.post(
        `${API}/admin/immigration-case/analyze`,
        { case_text: caseText, language: 'pl' },
        { withCredentials: true },
      );
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'analyze_failed');
    } finally {
      setLoading(false);
    }
  }, [caseText]);

  const handleClear = useCallback(() => {
    setCaseText('');
    setResult(null);
    setError(null);
    setCopyState('idle');
    setSaveState('idle');
  }, []);

  // Dry-run review — POSTs to the read-only admin endpoint that composes
  // the Timeline + Missing-doc + Case Review Packet engines. This endpoint
  // is read-only on the server (no status transition, no sign-off, no
  // version write, no audit-chain append). The frontend MUST NOT call any
  // sign-off / status-transition / create / update endpoint from this
  // handler. The button never runs automatically — only on explicit click.
  const handlePreviewDryRun = useCallback(async () => {
    if (!caseRecord || !caseRecord.case_id) return;
    setDryRunState('loading');
    setDryRunPacket(null);
    setDryRunCopyState('idle');
    try {
      // Pass `commentary=auto` so the backend attempts the LLM-assisted
      // commentary overlay; on any failure the backend returns the
      // deterministic fallback automatically. `language` mirrors the
      // current i18n locale so the disclaimer text matches the UI.
      const lang = (i18n.language || 'en').toLowerCase().startsWith('pl') ? 'pl' : 'en';
      const r = await axios.post(
        `${API}/admin/immigration-cases/${caseRecord.case_id}/dry-run-review`
        + `?commentary=auto&language=${lang}`,
        null,
        { withCredentials: true },
      );
      setDryRunPacket(r.data);
      setDryRunState('loaded');
    } catch (_e) {
      // Never echo backend internals into the UI; render the safe i18n
      // error message and stop. No stack trace, no secret leak.
      setDryRunPacket(null);
      setDryRunState('error');
    }
  }, [caseRecord, i18n.language]);

  const handleCopyDryRunMarkdown = useCallback(async () => {
    const md = (dryRunPacket && dryRunPacket.packet && dryRunPacket.packet.markdown) || '';
    if (!md) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(md);
        setDryRunCopyState('copied');
        setTimeout(
          () => setDryRunCopyState((s) => (s === 'copied' ? 'idle' : s)),
          2500,
        );
        return;
      }
      setDryRunCopyState('fallback');
    } catch (_) {
      setDryRunCopyState('fallback');
    }
  }, [dryRunPacket]);

  // Signed-off PDF download — GETs the admin-gated endpoint that enforces
  // the locked signed-off predicate server-side and returns a PDF blob.
  // The frontend MUST NOT call any sign-off / status-transition / create
  // / update endpoint from this handler. The button never runs
  // automatically — only on explicit click, and only renders when the
  // persisted record satisfies ``isSignedOffRecord``.
  const handleDownloadSignedOffPdf = useCallback(async () => {
    if (!caseRecord || !caseRecord.case_id) return;
    if (!isSignedOffRecord(caseRecord)) return;
    setPdfDownloadState('loading');
    try {
      const resp = await axios.get(
        `${API}/admin/immigration-cases/${caseRecord.case_id}/review-packet.pdf`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blob = new Blob(
        [resp.data],
        { type: resp.headers['content-type'] || 'application/pdf' },
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `hussarai-review-packet-${caseRecord.case_id}.pdf`;
      anchor.rel = 'noopener';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      setPdfDownloadState('idle');
    } catch (_e) {
      // Never echo backend internals into the UI.
      setPdfDownloadState('error');
    }
  }, [caseRecord]);

  // Audit-chain verification handler — read-only.
  // GETs the verify endpoint, displays the report. Never reaches any
  // sign-off / status / assign / dry-run / PDF endpoint.
  const handleVerifyAuditChain = useCallback(async () => {
    if (!caseRecord || !caseRecord.case_id) return;
    setAuditVerifyState('loading');
    setAuditVerifyReport(null);
    try {
      const r = await axios.get(
        `${API}/admin/immigration-cases/${caseRecord.case_id}/verify-audit-chain`,
        { withCredentials: true },
      );
      setAuditVerifyReport(r.data);
      setAuditVerifyState('loaded');
    } catch (_e) {
      setAuditVerifyReport(null);
      setAuditVerifyState('error');
    }
  }, [caseRecord]);

  // Amendment-draft submission handler — POSTs to the canonical
  // ``/amendments`` admin endpoint that creates a brand-new draft case
  // linked to the signed-off original. The original signed-off case
  // is NEVER mutated. This handler:
  //   * fires only on explicit form submit (no useEffect)
  //   * is gated on ``isSignedOffRecord(caseRecord)``
  //   * does NOT touch sign-off, status, assign, dry-run, PDF or email
  //     endpoints
  //   * never echoes backend internals into the UI — error messages are
  //     resolved from the i18n bundle by the safe error code returned
  //     by the backend
  const resolveAmendmentError = useCallback((errorCode) => {
    const known = [
      'signed_off_required_for_amendment',
      'amendment_reason_required',
      'amendment_reason_too_long',
      'amendment_scope_rejected',
      'unauthorized',
    ];
    const key = known.includes(errorCode) ? errorCode : 'generic';
    return t(`caseHubAdmin.amendmentDraft.errors.${key}`);
  }, [t]);

  const handleSubmitAmendmentDraft = useCallback(async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (!caseRecord || !caseRecord.case_id) return;
    if (!isSignedOffRecord(caseRecord)) return;
    setAmendmentError(null);

    const reason = (amendmentReason || '').trim();
    if (!reason) {
      setAmendmentError(resolveAmendmentError('amendment_reason_required'));
      return;
    }

    setAmendmentState('loading');
    try {
      const body = { amendment_reason: reason };
      const scopeText = (amendmentScope || '').trim();
      if (scopeText) {
        // The scope textarea is free-form notes; we wrap it into the
        // structured ``notes`` field so the backend sensitive-field guard
        // still applies and the categories allow-list stays empty (the
        // backend tolerates a scope without categories).
        body.amendment_scope = { notes: scopeText };
      }
      const r = await axios.post(
        `${API}/admin/immigration-cases/${caseRecord.case_id}/amendments`,
        body,
        { withCredentials: true },
      );
      setAmendmentResult(r.data);
      setAmendmentState('success');
    } catch (err) {
      const status = err && err.response ? err.response.status : null;
      const detail = err && err.response && err.response.data
        ? err.response.data.detail : null;
      const code = (detail && typeof detail === 'object') ? detail.code : null;
      if (status === 401 || status === 403) {
        setAmendmentError(resolveAmendmentError('unauthorized'));
      } else if (status === 409 && code) {
        setAmendmentError(resolveAmendmentError(code));
      } else if (status === 400 && code) {
        setAmendmentError(resolveAmendmentError(code));
      } else if (status === 422) {
        setAmendmentError(resolveAmendmentError('amendment_reason_required'));
      } else {
        setAmendmentError(resolveAmendmentError('generic'));
      }
      setAmendmentState('error');
    }
  }, [caseRecord, amendmentReason, amendmentScope, resolveAmendmentError]);

  const handleOpenAmendmentPanel = useCallback(() => {
    setAmendmentPanelOpen(true);
    setAmendmentResult(null);
    setAmendmentError(null);
    setAmendmentState('idle');
  }, []);

  const handleCloseAmendmentPanel = useCallback(() => {
    setAmendmentPanelOpen(false);
    setAmendmentReason('');
    setAmendmentScope('');
    setAmendmentResult(null);
    setAmendmentError(null);
    setAmendmentState('idle');
  }, []);

  return (
    <div className="hussar-landing" data-testid="immigration-case-dashboard-page" style={{ minHeight: '100vh' }}>
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px 80px' }}>
        <AdminStatusStrip />

        {/* Persisted-case loader strip — only renders when a ?case_id=...
            URL param triggered the loader. Loaded notice + error message
            are i18n-safe; no backend internals are echoed. */}
        {loaderState !== 'idle' && (
          <div
            data-testid="casehub-loader-strip"
            data-loader-state={loaderState}
            style={{
              margin: '0 0 14px',
              padding: '8px 12px',
              borderRadius: 8,
              fontSize: 12,
              letterSpacing: 0.2,
              border: loaderState === 'error'
                ? '1px solid rgba(248,113,113,0.36)'
                : '1px solid rgba(56,189,248,0.32)',
              background: loaderState === 'error'
                ? 'rgba(248,113,113,0.06)'
                : 'rgba(56,189,248,0.06)',
              color: loaderState === 'error' ? '#F87171' : '#38BDF8',
            }}
          >
            {loaderState === 'loading' && (
              <span data-testid="casehub-loader-loading">
                {t('caseHubAdmin.loader.loadingLabel')}
              </span>
            )}
            {loaderState === 'loaded' && (
              <span data-testid="casehub-loader-loaded">
                {t('caseHubAdmin.loader.loadedNote')}
              </span>
            )}
            {loaderState === 'error' && (
              <span data-testid="casehub-loader-error">
                {t('caseHubAdmin.loader.errorLabel')}
              </span>
            )}
          </div>
        )}

        <header style={{ margin: '12px 0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: 0.7, fontSize: 11, letterSpacing: 0.5 }}>
            <ShieldCheck className="w-3 h-3" /> {t('caseHubAdmin.eyebrow')}
          </div>
          <h1
            style={{ fontSize: 32, fontWeight: 600, margin: '8px 0 4px' }}
            data-testid="casehub-page-title"
          >
            {t('caseHubAdmin.pageTitle')}
          </h1>
          <p style={{ fontSize: 14, opacity: 0.8, margin: 0 }}>
            {t('caseHubAdmin.subtitle')}
          </p>
          <p
            data-testid="casehub-doctrine"
            style={{
              marginTop: 12,
              padding: '10px 14px',
              borderRadius: 6,
              background: 'rgba(125, 211, 252, 0.06)',
              borderLeft: '3px solid #7DD3FC',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {t('caseHubAdmin.doctrineLine')}
          </p>
        </header>

        {/* Input card */}
        <Card style={{ padding: 18, marginBottom: 20 }} data-testid="casehub-input-card">
          {/* Example-case insert — populates textarea, NEVER auto-submits */}
          <div
            data-testid="casehub-examples-row"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              flexWrap: 'wrap', marginBottom: 14,
              paddingBottom: 12,
              borderBottom: '1px dashed rgba(255,255,255,0.06)',
            }}
          >
            <Sparkles className="w-3 h-3" style={{ opacity: 0.7 }} aria-hidden="true" />
            <Select
              value=""
              onValueChange={handleInsertExample}
            >
              <SelectTrigger
                data-testid="casehub-example-select-trigger"
                style={{ width: 280, height: 32, fontSize: 12 }}
              >
                <SelectValue placeholder={t('caseHubAdmin.examples.insertLabel')} />
              </SelectTrigger>
              <SelectContent>
                {exampleItems.map((ex) => (
                  <SelectItem
                    key={ex.key}
                    value={ex.key}
                    data-testid={`casehub-example-item-${ex.key}`}
                  >
                    {ex.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span
              data-testid="casehub-examples-helper"
              style={{ fontSize: 11, opacity: 0.6, fontStyle: 'italic' }}
            >
              {t('caseHubAdmin.examples.helperText')}
            </span>
          </div>

          <label
            htmlFor="case-text"
            style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}
          >
            {t('caseHubAdmin.inputLabel')}
          </label>
          <Textarea
            id="case-text"
            data-testid="casehub-input-textarea"
            placeholder={t('caseHubAdmin.inputPlaceholder')}
            value={caseText}
            onChange={(e) => setCaseText(e.target.value)}
            rows={10}
            style={{ width: '100%', resize: 'vertical', minHeight: 180 }}
          />
          <div
            style={{
              marginTop: 10, display: 'flex', alignItems: 'center', gap: 10,
              flexWrap: 'wrap', justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: 11, opacity: 0.6 }}>
              {t('caseHubAdmin.minLengthHint')}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="sm"
                variant="outline"
                onClick={handleClear}
                disabled={loading || (!caseText && !result)}
                data-testid="casehub-clear-btn"
              >
                <Trash2 className="w-3 h-3 mr-1" /> {t('caseHubAdmin.clearBtn')}
              </Button>
              <Button
                size="sm"
                onClick={handleAnalyze}
                disabled={!canSubmit}
                className="hussar-btn-primary"
                data-testid="casehub-analyze-btn"
              >
                {loading ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <FileSearch className="w-3 h-3 mr-1" />
                )}
                {loading ? t('caseHubAdmin.analyzing') : t('caseHubAdmin.analyzeBtn')}
              </Button>
            </div>
          </div>
        </Card>

        {error && (
          <Card
            data-testid="casehub-error"
            style={{ padding: 14, marginBottom: 16, background: 'rgba(239,68,68,0.06)', borderLeft: '3px solid #F87171' }}
          >
            <AlertTriangle className="w-4 h-4 inline mr-2" style={{ color: '#F87171' }} />
            <span style={{ fontSize: 13 }}>{String(error)}</span>
          </Card>
        )}

        {/* Empty state */}
        {!result && !loading && !error && (
          <Card style={{ padding: 22, textAlign: 'center', opacity: 0.7 }} data-testid="casehub-empty">
            {t('caseHubAdmin.emptyState')}
          </Card>
        )}

        {/* Persisted-record pill — visible whenever a persisted caseRecord
            was loaded but no analysis is in progress. The pill mirrors the
            existing pill block inside the {result && (…)} branch, so the
            persisted status is reachable end-to-end without analyzing. */}
        {caseRecord && !result && (
          <div data-testid="casehub-persisted-record-strip" style={{ marginBottom: 14 }}>
            {isSignedOffRecord(caseRecord) ? (
              <div
                data-testid="casehub-signed-off-label-persisted"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 12px', borderRadius: 999,
                  background: 'rgba(34,197,94,0.10)',
                  border: '1px solid rgba(34,197,94,0.36)',
                  color: '#22C55E',
                  fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                }}
                title={caseRecord.payload_hash || ''}
              >
                <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
                {t('caseHubAdmin.signedOff.statusPillLabel')}
              </div>
            ) : (
              <div
                data-testid="casehub-working-brief-label-persisted"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 12px', borderRadius: 999,
                  background: 'rgba(245,158,11,0.08)',
                  border: '1px solid rgba(245,158,11,0.32)',
                  color: '#F59E0B',
                  fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                }}
              >
                <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                {t('caseHubAdmin.workingBrief.label')}
              </div>
            )}
          </div>
        )}

        {/* Amendment back-link — renders ONLY for cases whose latest
            version payload carries ``amends_case_id``. The link uses
            the existing /dashboard-akt-imigracyjnych?case_id=... loader
            so navigating it is purely read-only — no endpoint is called
            on render, only when the admin clicks the link the loader
            picks up the new ``case_id`` via the standard mount path. */}
        {caseRecord && !result && typeof caseRecord.amends_case_id === 'string'
          && caseRecord.amends_case_id && (
          <div
            data-testid="casehub-amends-backlink-row"
            style={{
              marginBottom: 14,
              padding: '10px 12px',
              background: 'rgba(56,189,248,0.06)',
              border: '1px solid rgba(56,189,248,0.30)',
              borderLeft: '3px solid #38BDF8',
              borderRadius: 8,
              display: 'flex', alignItems: 'flex-start', gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <p
              data-testid="casehub-amends-backlink-helper"
              style={{
                fontSize: 11, lineHeight: 1.55, margin: 0,
                color: 'rgba(226,232,240,0.78)', flex: '1 1 280px',
              }}
            >
              {t('caseHubAdmin.amendsBackLink.helperText')}
            </p>
            <a
              data-testid="casehub-amends-backlink"
              href={`/dashboard-akt-imigracyjnych?case_id=${caseRecord.amends_case_id}`}
              style={{
                fontSize: 12, fontWeight: 700,
                color: '#38BDF8', textDecoration: 'underline',
                flexShrink: 0,
              }}
            >
              {t('caseHubAdmin.amendsBackLink.label')}
            </a>
          </div>
        )}

        {/* Dry-run review preview — read-only composer over the existing
            Timeline + Missing-document + Case Review Packet engines via
            POST /api/admin/immigration-cases/{case_id}/dry-run-review.
            Renders only when a persisted ``caseRecord`` is loaded so the
            reviewer is acting on a real persisted case. Never auto-runs.
            Never mutates persistence. Never signs off. */}
        {caseRecord && caseRecord.case_id && (
          <Card
            data-testid="casehub-dry-run-review-card"
            style={{
              padding: 16, marginBottom: 16,
              background: 'rgba(125,211,252,0.03)',
              border: '1px solid rgba(125,211,252,0.18)',
            }}
          >
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                flexWrap: 'wrap', justifyContent: 'space-between',
              }}
            >
              <div>
                <h3
                  data-testid="casehub-dry-run-review-heading"
                  style={{ fontSize: 14, fontWeight: 700, margin: '0 0 6px' }}
                >
                  {t('caseHubAdmin.dryRunReview.panelHeading')}
                </h3>
                <p
                  data-testid="casehub-dry-run-review-safety"
                  style={{
                    fontSize: 12, opacity: 0.78, margin: 0, maxWidth: 640,
                    lineHeight: 1.5,
                  }}
                >
                  <AlertTriangle
                    className="w-3 h-3 inline mr-1"
                    style={{ verticalAlign: '-2px' }}
                    aria-hidden="true"
                  />
                  {t('caseHubAdmin.dryRunReview.safety')}
                </p>
              </div>
              <Button
                size="sm"
                onClick={handlePreviewDryRun}
                disabled={dryRunState === 'loading'}
                className="hussar-btn-primary"
                data-testid="casehub-dry-run-review-btn"
              >
                {dryRunState === 'loading' ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <FileSearch className="w-3 h-3 mr-1" />
                )}
                {dryRunState === 'loading'
                  ? t('caseHubAdmin.dryRunReview.loadingButton')
                  : t('caseHubAdmin.dryRunReview.button')}
              </Button>
            </div>

            {/* Signed-off PDF download — eligibility-gated. The button
                appears ONLY when the persisted record satisfies the locked
                ``isSignedOffRecord`` predicate (four HMAC-verified fields).
                The server re-enforces the same predicate and returns
                409 ``signed_off_required_for_pdf_export`` otherwise. */}
            {isSignedOffRecord(caseRecord) && (
              <div
                data-testid="casehub-signed-off-pdf-row"
                style={{
                  marginTop: 10,
                  display: 'flex', alignItems: 'center', gap: 10,
                  flexWrap: 'wrap', justifyContent: 'flex-end',
                }}
              >
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDownloadSignedOffPdf}
                  disabled={pdfDownloadState === 'loading'}
                  data-testid="casehub-signed-off-pdf-btn"
                >
                  {pdfDownloadState === 'loading' ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Download className="w-3 h-3 mr-1" />
                  )}
                  {pdfDownloadState === 'loading'
                    ? t('caseHubAdmin.signedOffPdf.downloading')
                    : t('caseHubAdmin.signedOffPdf.button')}
                </Button>
                {pdfDownloadState === 'error' && (
                  <span
                    data-testid="casehub-signed-off-pdf-error"
                    style={{
                      fontSize: 11, color: '#F87171', maxWidth: 320,
                      lineHeight: 1.4,
                    }}
                  >
                    <AlertTriangle
                      className="w-3 h-3 inline mr-1"
                      style={{ verticalAlign: '-2px' }}
                      aria-hidden="true"
                    />
                    {t('caseHubAdmin.signedOffPdf.errorMessage')}
                  </span>
                )}
              </div>
            )}

            {/* Amendment workflow — eligibility-gated, signed-off-only.
                Clicking opens a panel with a reason + optional scope field
                that POSTs to /api/admin/immigration-cases/{id}/amendments.
                The endpoint creates a brand-new draft case linked to the
                original signed-off case; the original is NEVER mutated.
                NEVER auto-fires: only renders on explicit click. */}
            {isSignedOffRecord(caseRecord) && (
              <div
                data-testid="casehub-amendment-row"
                style={{
                  marginTop: 14, paddingTop: 14,
                  borderTop: '1px dashed rgba(148,163,184,0.30)',
                }}
              >
                {!amendmentPanelOpen && amendmentState !== 'success' && (
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      flexWrap: 'wrap', justifyContent: 'space-between',
                    }}
                  >
                    <p
                      data-testid="casehub-amendment-row-intro"
                      style={{
                        fontSize: 11, lineHeight: 1.5,
                        color: 'rgba(226,232,240,0.72)', maxWidth: 540,
                        margin: 0,
                      }}
                    >
                      {t('caseHubAdmin.amendmentDraft.rowIntro')}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleOpenAmendmentPanel}
                      data-testid="casehub-amendment-open-btn"
                    >
                      <FilePlus className="w-3 h-3 mr-1" aria-hidden="true" />
                      {t('caseHubAdmin.amendmentDraft.openButton')}
                    </Button>
                  </div>
                )}

                {amendmentPanelOpen && amendmentState !== 'success' && (
                  <form
                    data-testid="casehub-amendment-form"
                    onSubmit={handleSubmitAmendmentDraft}
                    style={{
                      marginTop: 4,
                      padding: '14px 14px',
                      background: 'rgba(245,158,11,0.05)',
                      border: '1px solid rgba(245,158,11,0.30)',
                      borderLeft: '3px solid #F59E0B',
                      borderRadius: 10,
                      display: 'flex', flexDirection: 'column', gap: 12,
                    }}
                    noValidate
                  >
                    <div
                      data-testid="casehub-amendment-heading"
                      style={{
                        fontSize: 13, fontWeight: 700, color: '#F59E0B',
                      }}
                    >
                      {t('caseHubAdmin.amendmentDraft.heading')}
                    </div>
                    <p
                      data-testid="casehub-amendment-helper-text"
                      style={{
                        fontSize: 11, lineHeight: 1.5,
                        color: 'rgba(226,232,240,0.78)', margin: 0,
                      }}
                    >
                      {t('caseHubAdmin.amendmentDraft.helperText')}
                    </p>

                    {/* Required reason textarea */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label
                        htmlFor="casehub-amendment-reason"
                        style={{ fontSize: 11, fontWeight: 700, color: '#CBD5E1' }}
                      >
                        {t('caseHubAdmin.amendmentDraft.reasonLabel')}
                      </label>
                      <textarea
                        id="casehub-amendment-reason"
                        data-testid="casehub-amendment-reason-input"
                        required
                        rows={3}
                        maxLength={1000}
                        value={amendmentReason}
                        onChange={(ev) => setAmendmentReason(ev.target.value)}
                        placeholder={t('caseHubAdmin.amendmentDraft.reasonPlaceholder')}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: 8,
                          background: 'rgba(15,23,42,0.85)',
                          border: '1px solid rgba(245,158,11,0.32)',
                          color: '#E2E8F0',
                          fontSize: 13, lineHeight: 1.5,
                          resize: 'vertical',
                          fontFamily: 'inherit',
                        }}
                      />
                    </div>

                    {/* Optional scope textarea (free-form notes) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label
                        htmlFor="casehub-amendment-scope"
                        style={{ fontSize: 11, fontWeight: 700, color: '#CBD5E1' }}
                      >
                        {t('caseHubAdmin.amendmentDraft.scopeLabel')}
                      </label>
                      <textarea
                        id="casehub-amendment-scope"
                        data-testid="casehub-amendment-scope-input"
                        rows={2}
                        maxLength={3000}
                        value={amendmentScope}
                        onChange={(ev) => setAmendmentScope(ev.target.value)}
                        placeholder={t('caseHubAdmin.amendmentDraft.scopePlaceholder')}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: 8,
                          background: 'rgba(15,23,42,0.85)',
                          border: '1px solid rgba(148,163,184,0.32)',
                          color: '#E2E8F0',
                          fontSize: 13, lineHeight: 1.5,
                          resize: 'vertical',
                          fontFamily: 'inherit',
                        }}
                      />
                      <span
                        style={{
                          fontSize: 10, color: 'rgba(226,232,240,0.55)',
                          lineHeight: 1.4,
                        }}
                      >
                        {t('caseHubAdmin.amendmentDraft.scopeHelper')}
                      </span>
                    </div>

                    {/* Pinned safety disclaimer */}
                    <p
                      data-testid="casehub-amendment-disclaimer"
                      style={{
                        fontSize: 11, lineHeight: 1.55, margin: 0,
                        color: 'rgba(226,232,240,0.72)',
                        fontStyle: 'italic',
                        padding: '6px 10px',
                        background: 'rgba(15,23,42,0.55)',
                        border: '1px dashed rgba(148,163,184,0.32)',
                        borderRadius: 6,
                      }}
                    >
                      {t('caseHubAdmin.amendmentDraft.disclaimer')}
                    </p>

                    {amendmentError && (
                      <div
                        data-testid="casehub-amendment-error"
                        role="alert"
                        style={{
                          padding: '8px 12px',
                          background: 'rgba(248,113,113,0.08)',
                          border: '1px solid rgba(248,113,113,0.32)',
                          borderRadius: 6,
                          color: '#FCA5A5',
                          fontSize: 12, lineHeight: 1.5,
                        }}
                      >
                        {amendmentError}
                      </div>
                    )}

                    <div
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Button
                        type="submit"
                        size="sm"
                        variant="outline"
                        disabled={amendmentState === 'loading'}
                        data-testid="casehub-amendment-submit-btn"
                      >
                        {amendmentState === 'loading' ? (
                          <Loader2
                            className="w-3 h-3 mr-1 animate-spin"
                            aria-hidden="true"
                          />
                        ) : (
                          <FilePlus className="w-3 h-3 mr-1" aria-hidden="true" />
                        )}
                        {amendmentState === 'loading'
                          ? t('caseHubAdmin.amendmentDraft.submitting')
                          : t('caseHubAdmin.amendmentDraft.submitButton')}
                      </Button>
                      <button
                        type="button"
                        onClick={handleCloseAmendmentPanel}
                        data-testid="casehub-amendment-cancel-btn"
                        disabled={amendmentState === 'loading'}
                        style={{
                          background: 'transparent', border: 'none',
                          color: 'rgba(226,232,240,0.65)', fontSize: 12,
                          cursor: amendmentState === 'loading'
                            ? 'not-allowed' : 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        {t('caseHubAdmin.amendmentDraft.cancelButton')}
                      </button>
                    </div>
                  </form>
                )}

                {amendmentState === 'success' && amendmentResult && (
                  <div
                    data-testid="casehub-amendment-success"
                    style={{
                      marginTop: 4,
                      padding: '14px 14px',
                      background: 'rgba(34,197,94,0.06)',
                      border: '1px solid rgba(34,197,94,0.32)',
                      borderLeft: '3px solid #22C55E',
                      borderRadius: 10,
                    }}
                  >
                    <p
                      data-testid="casehub-amendment-success-title"
                      style={{
                        fontSize: 13, fontWeight: 700, margin: 0,
                        color: '#86EFAC',
                      }}
                    >
                      {t('caseHubAdmin.amendmentDraft.successTitle')}
                    </p>
                    <dl
                      style={{
                        marginTop: 10,
                        display: 'grid',
                        gridTemplateColumns: '180px 1fr',
                        gap: '4px 12px',
                        fontSize: 12,
                        color: 'rgba(226,232,240,0.88)',
                      }}
                    >
                      <dt style={{ opacity: 0.7 }}>
                        {t('caseHubAdmin.amendmentDraft.successOriginalCaseLabel')}
                      </dt>
                      <dd
                        data-testid="casehub-amendment-success-original-id"
                        style={{ margin: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}
                      >
                        {amendmentResult.original_case_id}
                      </dd>
                      <dt style={{ opacity: 0.7 }}>
                        {t('caseHubAdmin.amendmentDraft.successAmendmentCaseLabel')}
                      </dt>
                      <dd
                        data-testid="casehub-amendment-success-amendment-id"
                        style={{ margin: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}
                      >
                        {amendmentResult.amendment_case_id}
                      </dd>
                      <dt style={{ opacity: 0.7 }}>
                        {t('caseHubAdmin.amendmentDraft.successStatusLabel')}
                      </dt>
                      <dd
                        data-testid="casehub-amendment-success-status"
                        style={{ margin: 0 }}
                      >
                        {amendmentResult.status}
                      </dd>
                      <dt style={{ opacity: 0.7 }}>
                        {t('caseHubAdmin.amendmentDraft.successAmendsVersionLabel')}
                      </dt>
                      <dd
                        data-testid="casehub-amendment-success-amends-version"
                        style={{ margin: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}
                      >
                        {amendmentResult.amends_version_id || '—'}
                      </dd>
                    </dl>
                    <p
                      data-testid="casehub-amendment-success-disclaimer"
                      style={{
                        marginTop: 10,
                        fontSize: 11, lineHeight: 1.5,
                        color: 'rgba(226,232,240,0.72)',
                        fontStyle: 'italic',
                        margin: '10px 0 0',
                      }}
                    >
                      {amendmentResult.disclaimer}
                    </p>
                    <div
                      style={{
                        marginTop: 10,
                        display: 'flex', gap: 10, flexWrap: 'wrap',
                      }}
                    >
                      <a
                        href={`/dashboard-akt-imigracyjnych?case_id=${amendmentResult.amendment_case_id}`}
                        data-testid="casehub-amendment-success-load-link"
                        style={{
                          fontSize: 12, color: '#38BDF8',
                          textDecoration: 'underline',
                        }}
                      >
                        {t('caseHubAdmin.amendmentDraft.successLoadAmendmentLink')}
                      </a>
                      <button
                        type="button"
                        onClick={handleCloseAmendmentPanel}
                        data-testid="casehub-amendment-success-close-btn"
                        style={{
                          background: 'transparent', border: 'none',
                          color: 'rgba(226,232,240,0.65)', fontSize: 12,
                          cursor: 'pointer', textDecoration: 'underline',
                        }}
                      >
                        {t('caseHubAdmin.amendmentDraft.closeButton')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Audit-chain verification — GET /verify-audit-chain.
                Read-only, no mutation. Available on every persisted
                case (signed-off or not) so reviewers can confirm chain
                integrity at any state. */}
            <div
              data-testid="casehub-audit-verify-row"
              style={{
                marginTop: 10,
                display: 'flex', alignItems: 'center', gap: 10,
                flexWrap: 'wrap', justifyContent: 'flex-end',
              }}
            >
              <Button
                size="sm"
                variant="outline"
                onClick={handleVerifyAuditChain}
                disabled={auditVerifyState === 'loading'}
                data-testid="casehub-audit-verify-btn"
              >
                {auditVerifyState === 'loading' ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <ShieldCheck className="w-3 h-3 mr-1" />
                )}
                {auditVerifyState === 'loading'
                  ? t('caseHubAdmin.auditChainVerify.loadingButton')
                  : t('caseHubAdmin.auditChainVerify.button')}
              </Button>
            </div>

            {auditVerifyState === 'error' && (
              <div
                data-testid="casehub-audit-verify-error"
                style={{
                  marginTop: 10, padding: 10, borderRadius: 6,
                  background: 'rgba(248,113,113,0.06)',
                  border: '1px solid rgba(248,113,113,0.32)',
                  color: '#F87171', fontSize: 12,
                }}
              >
                <AlertTriangle
                  className="w-3 h-3 inline mr-1"
                  style={{ verticalAlign: '-2px' }}
                  aria-hidden="true"
                />
                {t('caseHubAdmin.auditChainVerify.errorMessage')}
              </div>
            )}

            {auditVerifyState === 'loaded'
              && auditVerifyReport
              && auditVerifyReport.verification && (
              <div
                data-testid="casehub-audit-verify-panel"
                style={{
                  marginTop: 12, padding: 12, borderRadius: 6,
                  background: 'rgba(125,211,252,0.03)',
                  border: '1px solid rgba(125,211,252,0.18)',
                }}
              >
                <h4
                  style={{
                    fontSize: 13, fontWeight: 700, margin: '0 0 8px',
                  }}
                >
                  {t('caseHubAdmin.auditChainVerify.panelHeading')}
                </h4>
                <p
                  data-testid="casehub-audit-verify-safety"
                  style={{
                    fontSize: 12, opacity: 0.78, margin: '0 0 10px',
                    lineHeight: 1.5,
                  }}
                >
                  <AlertTriangle
                    className="w-3 h-3 inline mr-1"
                    style={{ verticalAlign: '-2px' }}
                    aria-hidden="true"
                  />
                  {t('caseHubAdmin.auditChainVerify.safety')}
                </p>

                <div
                  style={{
                    display: 'flex', gap: 14, flexWrap: 'wrap',
                    marginBottom: 10,
                  }}
                >
                  <span
                    data-testid="casehub-audit-verify-status"
                    style={{
                      fontSize: 11, padding: '4px 10px', borderRadius: 999,
                      background:
                        auditVerifyReport.verification.verification_status
                          === 'verified'
                          ? 'rgba(34,197,94,0.10)'
                          : auditVerifyReport.verification.verification_status
                              === 'verified_with_warnings'
                            ? 'rgba(245,158,11,0.10)'
                            : 'rgba(248,113,113,0.10)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      fontWeight: 700, letterSpacing: 0.4,
                    }}
                  >
                    {t('caseHubAdmin.auditChainVerify.statusHeading')}
                    {': '}
                    {t(
                      `caseHubAdmin.auditChainVerify.status${
                        {
                          verified: 'Verified',
                          verified_with_warnings: 'VerifiedWithWarnings',
                          failed: 'Failed',
                        }[
                          auditVerifyReport.verification.verification_status
                        ] || 'Failed'
                      }`,
                      auditVerifyReport.verification.verification_status || '',
                    )}
                  </span>
                  <span
                    data-testid="casehub-audit-verify-risk"
                    style={{
                      fontSize: 11, padding: '4px 10px', borderRadius: 999,
                      background: 'rgba(245,158,11,0.08)',
                      border: '1px solid rgba(245,158,11,0.32)',
                      color: '#F59E0B', fontWeight: 700, letterSpacing: 0.4,
                    }}
                  >
                    {t('caseHubAdmin.auditChainVerify.riskHeading')}
                    {': '}
                    {t(
                      `caseHubAdmin.severity.${
                        auditVerifyReport.verification.risk_level || 'medium'
                      }`,
                      auditVerifyReport.verification.risk_level || '',
                    )}
                  </span>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <div
                    style={{
                      fontSize: 11, letterSpacing: 0.5, opacity: 0.6,
                      marginBottom: 4, fontWeight: 600,
                    }}
                  >
                    {t('caseHubAdmin.auditChainVerify.summaryHeading')}
                  </div>
                  <p
                    data-testid="casehub-audit-verify-summary"
                    style={{ fontSize: 12, margin: 0, lineHeight: 1.5 }}
                  >
                    {String(auditVerifyReport.verification.summary || '')}
                  </p>
                </div>

                <ul
                  data-testid="casehub-audit-verify-flags"
                  style={{
                    margin: '0 0 10px', paddingLeft: 18, fontSize: 12,
                    lineHeight: 1.6,
                  }}
                >
                  <li data-testid="casehub-audit-verify-signed-off-flag">
                    {auditVerifyReport.verification.signed_off_verified
                      ? t('caseHubAdmin.auditChainVerify.signedOffVerified')
                      : t('caseHubAdmin.auditChainVerify.signedOffNotVerified')}
                  </li>
                  <li data-testid="casehub-audit-verify-current-version-flag">
                    {auditVerifyReport.verification.current_version_verified
                      ? t('caseHubAdmin.auditChainVerify.currentVersionVerified')
                      : t('caseHubAdmin.auditChainVerify.currentVersionNotVerified')}
                  </li>
                </ul>

                <div style={{ marginBottom: 10 }}>
                  <div
                    style={{
                      fontSize: 11, letterSpacing: 0.5, opacity: 0.6,
                      marginBottom: 4, fontWeight: 600,
                    }}
                  >
                    {t('caseHubAdmin.auditChainVerify.failuresHeading')}
                  </div>
                  {Array.isArray(auditVerifyReport.verification.failures)
                    && auditVerifyReport.verification.failures.length > 0 ? (
                    <ul
                      data-testid="casehub-audit-verify-failures"
                      style={{
                        margin: 0, paddingLeft: 18, fontSize: 12,
                        lineHeight: 1.6,
                      }}
                    >
                      {auditVerifyReport.verification.failures.map((f, i) => (
                        <li
                          key={`audit-fail-${i}`}
                          data-testid={`casehub-audit-verify-failure-${i}`}
                        >
                          <SeverityChip severity={(f && f.severity) || 'medium'} />{' '}
                          <code>{(f && f.code) || ''}</code>: {(f && f.message) || ''}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p
                      data-testid="casehub-audit-verify-failures-empty"
                      style={{ fontSize: 12, opacity: 0.6, margin: 0 }}
                    >
                      {t('caseHubAdmin.auditChainVerify.noFailures')}
                    </p>
                  )}
                </div>

                <div>
                  <div
                    style={{
                      fontSize: 11, letterSpacing: 0.5, opacity: 0.6,
                      marginBottom: 4, fontWeight: 600,
                    }}
                  >
                    {t('caseHubAdmin.auditChainVerify.warningsHeading')}
                  </div>
                  {Array.isArray(auditVerifyReport.verification.warnings)
                    && auditVerifyReport.verification.warnings.length > 0 ? (
                    <ul
                      data-testid="casehub-audit-verify-warnings"
                      style={{
                        margin: 0, paddingLeft: 18, fontSize: 12,
                        lineHeight: 1.6,
                      }}
                    >
                      {auditVerifyReport.verification.warnings.map((w, i) => (
                        <li
                          key={`audit-warn-${i}`}
                          data-testid={`casehub-audit-verify-warning-${i}`}
                        >
                          <SeverityChip severity={(w && w.severity) || 'low'} />{' '}
                          <code>{(w && w.code) || ''}</code>: {(w && w.message) || ''}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p
                      data-testid="casehub-audit-verify-warnings-empty"
                      style={{ fontSize: 12, opacity: 0.6, margin: 0 }}
                    >
                      {t('caseHubAdmin.auditChainVerify.noWarnings')}
                    </p>
                  )}
                </div>
              </div>
            )}




            {dryRunState === 'error' && (
              <div
                data-testid="casehub-dry-run-review-error"
                style={{
                  marginTop: 12, padding: 10, borderRadius: 6,
                  background: 'rgba(248,113,113,0.06)',
                  border: '1px solid rgba(248,113,113,0.32)',
                  color: '#F87171', fontSize: 12,
                }}
              >
                <AlertTriangle
                  className="w-3 h-3 inline mr-1"
                  style={{ verticalAlign: '-2px' }}
                  aria-hidden="true"
                />
                {t('caseHubAdmin.dryRunReview.errorMessage')}
              </div>
            )}

            {dryRunState === 'loaded' && dryRunPacket && dryRunPacket.packet && (
              <div
                data-testid="casehub-dry-run-review-panel"
                style={{ marginTop: 14 }}
              >
                {/* Risk summary */}
                <div
                  data-testid="casehub-dry-run-review-risk"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '4px 12px', borderRadius: 999,
                    background: 'rgba(245,158,11,0.08)',
                    border: '1px solid rgba(245,158,11,0.32)',
                    color: '#F59E0B',
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                    marginBottom: 12,
                  }}
                >
                  <ShieldAlert className="w-3 h-3" aria-hidden="true" />
                  {t('caseHubAdmin.dryRunReview.riskHeading')}
                  {': '}
                  <span data-testid="casehub-dry-run-review-risk-value">
                    {t(
                      `caseHubAdmin.severity.${dryRunPacket.packet.risk_summary || 'medium'}`,
                      dryRunPacket.packet.risk_summary || '',
                    )}
                  </span>
                </div>

                {/* Open questions for reviewer */}
                <div style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      fontSize: 11, letterSpacing: 0.5, opacity: 0.6,
                      marginBottom: 6, fontWeight: 600,
                    }}
                  >
                    {t('caseHubAdmin.dryRunReview.openQuestionsHeading')}
                  </div>
                  {Array.isArray(dryRunPacket.packet.open_questions_for_reviewer)
                    && dryRunPacket.packet.open_questions_for_reviewer.length > 0 ? (
                    <ul
                      data-testid="casehub-dry-run-review-open-questions"
                      style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.6 }}
                    >
                      {dryRunPacket.packet.open_questions_for_reviewer.map((q, i) => (
                        <li
                          key={`dry-q-${i}`}
                          data-testid={`casehub-dry-run-review-open-question-${i}`}
                        >
                          {String(q)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p
                      data-testid="casehub-dry-run-review-open-questions-empty"
                      style={{ fontSize: 12, opacity: 0.6, margin: 0 }}
                    >
                      {t('caseHubAdmin.dryRunReview.noOpenQuestions')}
                    </p>
                  )}
                </div>

                {/* Reviewer action items */}
                <div style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      fontSize: 11, letterSpacing: 0.5, opacity: 0.6,
                      marginBottom: 6, fontWeight: 600,
                    }}
                  >
                    {t('caseHubAdmin.dryRunReview.actionItemsHeading')}
                  </div>
                  {Array.isArray(dryRunPacket.packet.reviewer_action_items)
                    && dryRunPacket.packet.reviewer_action_items.length > 0 ? (
                    <ul
                      data-testid="casehub-dry-run-review-action-items"
                      style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.6 }}
                    >
                      {dryRunPacket.packet.reviewer_action_items.map((a, i) => (
                        <li
                          key={`dry-a-${i}`}
                          data-testid={`casehub-dry-run-review-action-item-${i}`}
                        >
                          <SeverityChip severity={(a && a.severity) || 'medium'} />{' '}
                          {(a && a.action) || ''}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p
                      data-testid="casehub-dry-run-review-action-items-empty"
                      style={{ fontSize: 12, opacity: 0.6, margin: 0 }}
                    >
                      {t('caseHubAdmin.dryRunReview.noActionItems')}
                    </p>
                  )}
                </div>

                {/* Markdown panel — read-only textarea so the reviewer can
                    select-and-copy manually as well, and a copy button. */}
                {dryRunPacket.commentary && (
                  <div
                    data-testid="casehub-dry-run-commentary"
                    style={{
                      marginTop: 4, marginBottom: 18,
                      padding: '14px 14px',
                      background: 'rgba(56,189,248,0.05)',
                      border: '1px solid rgba(56,189,248,0.24)',
                      borderLeft: '3px solid #38BDF8',
                      borderRadius: 10,
                    }}
                  >
                    {/* Heading + LLM-vs-fallback badge */}
                    <div
                      style={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', gap: 8,
                        flexWrap: 'wrap', marginBottom: 6,
                      }}
                    >
                      <div
                        data-testid="casehub-dry-run-commentary-heading"
                        style={{
                          fontSize: 13, fontWeight: 700, color: '#38BDF8',
                          letterSpacing: 0.2,
                        }}
                      >
                        {t('caseHubAdmin.dryRunReview.commentary.heading')}
                      </div>
                      <span
                        data-testid="casehub-dry-run-commentary-llm-badge"
                        style={{
                          fontSize: 10, letterSpacing: 0.6, fontWeight: 700,
                          padding: '2px 8px', borderRadius: 999,
                          background: dryRunPacket.commentary.llm_used
                            ? 'rgba(56,189,248,0.12)'
                            : 'rgba(148,163,184,0.12)',
                          border: '1px solid rgba(148,163,184,0.32)',
                          color: dryRunPacket.commentary.llm_used
                            ? '#38BDF8' : '#94A3B8',
                          textTransform: 'uppercase',
                        }}
                      >
                        {dryRunPacket.commentary.llm_used
                          ? t('caseHubAdmin.dryRunReview.commentary.llmUsedYes')
                          : t('caseHubAdmin.dryRunReview.commentary.llmUsedNo')}
                      </span>
                    </div>

                    {/* Authoritative source-of-truth reminder — pinned at top */}
                    <p
                      data-testid="casehub-dry-run-commentary-source-note"
                      style={{
                        fontSize: 11, lineHeight: 1.55,
                        color: 'rgba(226,232,240,0.78)',
                        margin: '0 0 12px 0',
                        padding: '6px 10px',
                        background: 'rgba(245,158,11,0.06)',
                        border: '1px solid rgba(245,158,11,0.28)',
                        borderRadius: 6,
                      }}
                    >
                      <ShieldAlert
                        className="w-3 h-3 inline mr-1"
                        style={{ verticalAlign: '-2px', color: '#F59E0B' }}
                        aria-hidden="true"
                      />
                      {t('caseHubAdmin.dryRunReview.commentary.sourceAuthorityNote')}
                    </p>

                    {/* Summary for reviewer */}
                    {dryRunPacket.commentary.summary_for_reviewer && (
                      <div style={{ marginBottom: 10 }}>
                        <div
                          style={{
                            fontSize: 10, letterSpacing: 0.5, opacity: 0.6,
                            marginBottom: 4, fontWeight: 700,
                            textTransform: 'uppercase',
                          }}
                        >
                          {t('caseHubAdmin.dryRunReview.commentary.summaryHeading')}
                        </div>
                        <p
                          data-testid="casehub-dry-run-commentary-summary"
                          style={{
                            fontSize: 12, lineHeight: 1.6, margin: 0,
                            color: 'rgba(226,232,240,0.92)',
                          }}
                        >
                          {dryRunPacket.commentary.summary_for_reviewer}
                        </p>
                      </div>
                    )}

                    {/* Plain-language overview */}
                    {dryRunPacket.commentary.plain_language_overview && (
                      <div style={{ marginBottom: 10 }}>
                        <div
                          style={{
                            fontSize: 10, letterSpacing: 0.5, opacity: 0.6,
                            marginBottom: 4, fontWeight: 700,
                            textTransform: 'uppercase',
                          }}
                        >
                          {t('caseHubAdmin.dryRunReview.commentary.overviewHeading')}
                        </div>
                        <p
                          data-testid="casehub-dry-run-commentary-overview"
                          style={{
                            fontSize: 12, lineHeight: 1.6, margin: 0,
                            color: 'rgba(226,232,240,0.85)',
                          }}
                        >
                          {dryRunPacket.commentary.plain_language_overview}
                        </p>
                      </div>
                    )}

                    {/* Key review points */}
                    <div style={{ marginBottom: 10 }}>
                      <div
                        style={{
                          fontSize: 10, letterSpacing: 0.5, opacity: 0.6,
                          marginBottom: 4, fontWeight: 700,
                          textTransform: 'uppercase',
                        }}
                      >
                        {t('caseHubAdmin.dryRunReview.commentary.keyPointsHeading')}
                      </div>
                      {Array.isArray(dryRunPacket.commentary.key_review_points)
                        && dryRunPacket.commentary.key_review_points.length > 0 ? (
                        <ul
                          data-testid="casehub-dry-run-commentary-key-points"
                          style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.6 }}
                        >
                          {dryRunPacket.commentary.key_review_points.map((p, i) => (
                            <li
                              key={`commentary-kp-${i}`}
                              data-testid={`casehub-dry-run-commentary-key-point-${i}`}
                            >
                              {String(p)}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p
                          data-testid="casehub-dry-run-commentary-key-points-empty"
                          style={{ fontSize: 12, opacity: 0.6, margin: 0 }}
                        >
                          {t('caseHubAdmin.dryRunReview.commentary.noKeyPoints')}
                        </p>
                      )}
                    </div>

                    {/* Questions to resolve */}
                    <div style={{ marginBottom: 10 }}>
                      <div
                        style={{
                          fontSize: 10, letterSpacing: 0.5, opacity: 0.6,
                          marginBottom: 4, fontWeight: 700,
                          textTransform: 'uppercase',
                        }}
                      >
                        {t('caseHubAdmin.dryRunReview.commentary.questionsHeading')}
                      </div>
                      {Array.isArray(dryRunPacket.commentary.questions_to_resolve)
                        && dryRunPacket.commentary.questions_to_resolve.length > 0 ? (
                        <ul
                          data-testid="casehub-dry-run-commentary-questions"
                          style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.6 }}
                        >
                          {dryRunPacket.commentary.questions_to_resolve.map((q, i) => (
                            <li
                              key={`commentary-q-${i}`}
                              data-testid={`casehub-dry-run-commentary-question-${i}`}
                            >
                              {String(q)}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p
                          data-testid="casehub-dry-run-commentary-questions-empty"
                          style={{ fontSize: 12, opacity: 0.6, margin: 0 }}
                        >
                          {t('caseHubAdmin.dryRunReview.commentary.noQuestions')}
                        </p>
                      )}
                    </div>

                    {/* Suggested next review steps */}
                    <div style={{ marginBottom: 10 }}>
                      <div
                        style={{
                          fontSize: 10, letterSpacing: 0.5, opacity: 0.6,
                          marginBottom: 4, fontWeight: 700,
                          textTransform: 'uppercase',
                        }}
                      >
                        {t('caseHubAdmin.dryRunReview.commentary.stepsHeading')}
                      </div>
                      {Array.isArray(dryRunPacket.commentary.suggested_next_review_steps)
                        && dryRunPacket.commentary.suggested_next_review_steps.length > 0 ? (
                        <ul
                          data-testid="casehub-dry-run-commentary-steps"
                          style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.6 }}
                        >
                          {dryRunPacket.commentary.suggested_next_review_steps.map((s_, i) => (
                            <li
                              key={`commentary-s-${i}`}
                              data-testid={`casehub-dry-run-commentary-step-${i}`}
                            >
                              {String(s_)}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p
                          data-testid="casehub-dry-run-commentary-steps-empty"
                          style={{ fontSize: 12, opacity: 0.6, margin: 0 }}
                        >
                          {t('caseHubAdmin.dryRunReview.commentary.noSteps')}
                        </p>
                      )}
                    </div>

                    {/* Limits + source authority + disclaimer — pinned bottom */}
                    <div
                      style={{
                        marginTop: 12, paddingTop: 10,
                        borderTop: '1px dashed rgba(148,163,184,0.30)',
                        display: 'grid', gap: 6,
                        fontSize: 11, lineHeight: 1.55,
                        color: 'rgba(226,232,240,0.72)',
                      }}
                    >
                      <div data-testid="casehub-dry-run-commentary-limits">
                        <span style={{ opacity: 0.7, fontWeight: 700 }}>
                          {t('caseHubAdmin.dryRunReview.commentary.limitsHeading')}:
                        </span>{' '}
                        {dryRunPacket.commentary.limits_of_ai_commentary}
                      </div>
                      <div data-testid="casehub-dry-run-commentary-source-authority">
                        <span style={{ opacity: 0.7, fontWeight: 700 }}>
                          {t('caseHubAdmin.dryRunReview.commentary.sourceAuthorityHeading')}:
                        </span>{' '}
                        {dryRunPacket.commentary.source_authority}
                      </div>
                      <div
                        data-testid="casehub-dry-run-commentary-disclaimer"
                        style={{ fontStyle: 'italic' }}
                      >
                        <span style={{ opacity: 0.7, fontWeight: 700, fontStyle: 'normal' }}>
                          {t('caseHubAdmin.dryRunReview.commentary.disclaimerHeading')}:
                        </span>{' '}
                        {dryRunPacket.commentary.disclaimer}
                      </div>
                    </div>
                  </div>
                )}

                {/* Markdown panel — read-only textarea so the reviewer can
                    select-and-copy manually as well, and a copy button. */}
                <div style={{ marginBottom: 10 }}>
                  <div
                    style={{
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', gap: 10,
                      flexWrap: 'wrap', marginBottom: 6,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11, letterSpacing: 0.5, opacity: 0.6,
                        fontWeight: 600,
                      }}
                    >
                      {t('caseHubAdmin.dryRunReview.markdownHeading')}
                    </div>
                    <Button
                      size="sm"
                      onClick={handleCopyDryRunMarkdown}
                      data-testid="casehub-dry-run-review-copy-btn"
                      className="hussar-btn-primary"
                    >
                      {dryRunCopyState === 'copied' ? (
                        <ClipboardCheck className="w-3 h-3 mr-1" />
                      ) : (
                        <Copy className="w-3 h-3 mr-1" />
                      )}
                      {dryRunCopyState === 'copied'
                        ? t('caseHubAdmin.dryRunReview.copied')
                        : t('caseHubAdmin.dryRunReview.copyButton')}
                    </Button>
                  </div>
                  <textarea
                    data-testid="casehub-dry-run-review-markdown"
                    readOnly
                    value={String(dryRunPacket.packet.markdown || '')}
                    rows={14}
                    style={{
                      width: '100%', fontFamily: 'monospace', fontSize: 11,
                      background: 'rgba(0,0,0,0.25)', padding: 10,
                      borderRadius: 6,
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: 'inherit', resize: 'vertical',
                    }}
                  />
                </div>

                {dryRunCopyState === 'fallback' && (
                  <p
                    data-testid="casehub-dry-run-review-copy-fallback"
                    style={{ fontSize: 12, opacity: 0.75, margin: '8px 0 0' }}
                  >
                    <AlertTriangle
                      className="w-3 h-3 inline mr-1"
                      style={{ verticalAlign: '-2px' }}
                      aria-hidden="true"
                    />
                    {t('caseHubAdmin.dryRunReview.fallback')}
                  </p>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Result */}
        {result && (
          <div data-testid="casehub-result">
            {/* Fallback banner */}
            {result.llm_used === false && (
              <Card
                data-testid="casehub-fallback-banner"
                style={{ padding: 14, marginBottom: 16, background: 'rgba(245,158,11,0.06)', borderLeft: '3px solid #F59E0B' }}
              >
                <AlertTriangle className="w-4 h-4 inline mr-2" style={{ color: '#F59E0B' }} />
                <span style={{ fontSize: 13 }}>
                  {t('caseHubAdmin.fallbackBanner')}{' '}
                  <code style={{ fontSize: 11, opacity: 0.7 }}>{result.fallback_reason || ''}</code>
                </span>
              </Card>
            )}

            {/* Doctrine banner */}
            <Card
              data-testid="casehub-result-doctrine"
              style={{
                padding: 14, marginBottom: 16,
                background: 'rgba(125,211,252,0.06)',
                borderLeft: '3px solid #7DD3FC',
                fontSize: 13, fontWeight: 600,
              }}
            >
              {result.doctrine_pl} <span style={{ opacity: 0.55 }}>· {result.doctrine_en}</span>
            </Card>

            {/* Status pill — defensive boundary marker.
                Defaults to "working brief / draft for human review".
                Switches to the audit-anchored pill ONLY when the persisted
                record carries the four locked HMAC-verified fields proving
                server-side human-authority closure. */}
            {isSignedOffRecord(caseRecord) ? (
              <div
                data-testid="casehub-signed-off-label"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 12px', borderRadius: 999,
                  background: 'rgba(34,197,94,0.10)',
                  border: '1px solid rgba(34,197,94,0.36)',
                  color: '#22C55E',
                  fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                  marginBottom: 14,
                }}
                title={caseRecord.payload_hash || ''}
              >
                <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
                {t('caseHubAdmin.signedOff.statusPillLabel')}
              </div>
            ) : (
              <div
                data-testid="casehub-working-brief-label"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 12px', borderRadius: 999,
                  background: 'rgba(245,158,11,0.08)',
                  border: '1px solid rgba(245,158,11,0.32)',
                  color: '#F59E0B',
                  fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                  marginBottom: 14,
                }}
              >
                <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                {t('caseHubAdmin.workingBrief.label')}
              </div>
            )}

            {/* 10 sections */}
            <div
              data-testid="casehub-sections-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: 14,
                marginBottom: 20,
              }}
            >
              {SECTION_KEYS.map((key) => (
                <Card
                  key={key}
                  data-testid={`casehub-section-${key}`}
                  style={{
                    padding: 16,
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>
                      {t(`caseHubAdmin.sectionLabels.${key}`)}
                    </h3>
                    <HumanReviewBadge />
                  </div>
                  {renderSection(key, result[key], t)}
                </Card>
              ))}
            </div>

            {/* Audit trail */}
            <Card
              data-testid="casehub-audit-card"
              style={{
                padding: 18,
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}
                  data-testid="casehub-audit-heading">
                {t('caseHubAdmin.auditTrailHeading')}
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                  gap: 14,
                }}
              >
                <div>
                  <div style={{ fontSize: 11, letterSpacing: 0.5, opacity: 0.6, marginBottom: 6, fontWeight: 600 }}>
                    {t('caseHubAdmin.auditTrailTextLabel')}
                  </div>
                  <pre
                    data-testid="casehub-audit-text"
                    style={{
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      background: 'rgba(0,0,0,0.25)', padding: 12, borderRadius: 6,
                      fontSize: 12, lineHeight: 1.55, margin: 0, maxHeight: 280, overflow: 'auto',
                    }}
                  >
                    {result.audit_trail_text || ''}
                  </pre>
                </div>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: 0.5, opacity: 0.6, marginBottom: 6, fontWeight: 600 }}>
                    {t('caseHubAdmin.auditTrailJsonLabel')}
                  </div>
                  <pre
                    data-testid="casehub-audit-json"
                    style={{
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      background: 'rgba(0,0,0,0.25)', padding: 12, borderRadius: 6,
                      fontSize: 11, lineHeight: 1.5, margin: 0, maxHeight: 280, overflow: 'auto',
                    }}
                  >
                    {JSON.stringify(result.audit_trail_timeline || [], null, 2)}
                  </pre>
                </div>
              </div>
            </Card>

            {/* Copy case brief — client-side Markdown only. Never hits backend. */}
            <Card
              data-testid="casehub-copy-brief-card"
              style={{
                padding: 16,
                marginTop: 16,
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  flexWrap: 'wrap', justifyContent: 'space-between',
                }}
              >
                <div style={{ fontSize: 12, opacity: 0.75, maxWidth: 540, lineHeight: 1.5 }}>
                  {t('caseHubAdmin.copyBrief.disclaimer')}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button
                    size="sm"
                    onClick={handleCopyBrief}
                    data-testid="casehub-copy-brief-btn"
                    className="hussar-btn-primary"
                  >
                    {copyState === 'copied' ? (
                      <ClipboardCheck className="w-3 h-3 mr-1" />
                    ) : (
                      <Copy className="w-3 h-3 mr-1" />
                    )}
                    {copyState === 'copied'
                      ? t('caseHubAdmin.copyBrief.copied')
                      : t('caseHubAdmin.copyBrief.button')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSaveBrief}
                    data-testid="casehub-save-brief-btn"
                  >
                    {saveState === 'saved' ? (
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                    ) : (
                      <Download className="w-3 h-3 mr-1" />
                    )}
                    {saveState === 'saved'
                      ? t('caseHubAdmin.saveBrief.saved')
                      : t('caseHubAdmin.saveBrief.button')}
                  </Button>
                </div>
              </div>

              {copyState === 'fallback' && (
                <div style={{ marginTop: 12 }} data-testid="casehub-copy-brief-fallback">
                  <p style={{ fontSize: 12, opacity: 0.75, margin: '0 0 8px' }}>
                    <AlertTriangle className="w-3 h-3 inline mr-1" style={{ verticalAlign: '-2px' }} />
                    {t('caseHubAdmin.copyBrief.fallback')}
                  </p>
                  <textarea
                    data-testid="casehub-copy-brief-fallback-textarea"
                    readOnly
                    value={briefMarkdown}
                    rows={10}
                    style={{
                      width: '100%', fontFamily: 'monospace', fontSize: 11,
                      background: 'rgba(0,0,0,0.25)', padding: 10, borderRadius: 6,
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: 'inherit', resize: 'vertical',
                    }}
                  />
                </div>
              )}
            </Card>

            <p
              data-testid="casehub-disclaimer"
              style={{
                marginTop: 16, fontSize: 12, opacity: 0.65,
                fontStyle: 'italic',
              }}
            >
              <AlertTriangle className="w-3 h-3 inline mr-1" style={{ verticalAlign: '-2px' }} />
              {result.disclaimer_pl}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ImmigrationCaseDashboard;
